"use client";
import { ChatType } from "@shared/index";
import { useRef, useEffect } from "react";
import { Logger } from "@shared/utils/logger";
import { playSound } from "@client/utils/sounds";
import type { ChatMessage } from "@client/types/meet.types";
import { Room, RoomEvent, RemoteParticipant } from "livekit-client";
import type { MeetingEndPayload, MuteRequestPayload } from "@client/types/livekit.types";

export interface FileChunkMessage {
  type: "file-chunk";
  id: string;
  fileId: string;
  chunkIndex: number;
  totalChunks: number;
  chunk: string;
  metadata: { name: string; size: number; type: string };
  senderId?: number;
}

export interface BaseDataMessage {
  type: string;
  id: string;
  senderId?: number;
  text?: string;
  file?: ChatMessage["file"];
}

export type DataMessage =
  | (BaseDataMessage & { type: "chat"; text: string })
  | (BaseDataMessage & { type: "file"; text: string; file: ChatMessage["file"] })
  | FileChunkMessage
  | (BaseDataMessage & MeetingEndPayload)
  | (BaseDataMessage & MuteRequestPayload)
  | (BaseDataMessage & Record<string, unknown>);

interface FileChunkData {
  chunks: string[];
  metadata: { name: string; size: number; type: string };
  totalChunks: number;
}

interface DataChannelHandlers {
  chat?: {
    addChatMessage: (message: ChatMessage) => void;
    incrementUnreadMessages: () => void;
  };
  reactions: {
    handleReactionDataReceived: (message: Record<string, unknown>, participant?: RemoteParticipant) => void;
  };
  polls: {
    handlePollDataReceived: (message: Record<string, unknown>) => void;
  };
  notes: {
    handleNotesDataReceived: (message: Record<string, unknown>) => void;
  };
  whiteboard: {
    handleWhiteboardDataReceived: (message: Record<string, unknown>, participant?: RemoteParticipant) => void;
  };
  joinRequests: {
    handleJoinRequestDataReceived: (message: Record<string, unknown>, participant?: RemoteParticipant) => void;
  };
  meetingEnd?: {
    handleMeetingEnd: (payload: MeetingEndPayload) => void;
  };
  muteRequest?: {
    handleMuteRequest: (payload: MuteRequestPayload) => void;
  };
  breakout?: {
    handleBreakoutDataReceived: (message: Record<string, unknown>) => void;
  };
  hostControls?: {
    handleHostMeetingControls: (message: Record<string, unknown>) => void;
    handleHostStopScreenShare: () => void;
  };
}

interface UseDataChannelManagerOptions {
  room: Room;
  showChat: boolean;
  handlers: DataChannelHandlers;
}

/**
 * usageDataChannelManager - Centralized handler for all incoming data channel events.
 * Optimized to minimize re-renders by using refs for handlers.
 */
export const useDataChannelManager = ({ room, showChat, handlers }: UseDataChannelManagerOptions): void => {
  // Use refs to avoid recreating the effect when handlers change
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;

  const showChatRef = useRef(showChat);
  showChatRef.current = showChat;

  // File chunks storage - persists across renders without causing re-renders
  const fileChunksRef = useRef<Map<string, FileChunkData>>(new Map());

  useEffect(() => {
    if (!room) return;

    /**
     * Reconstructs file from chunks and triggers chat handler when complete.
     */
    const handleFileChunk = (message: FileChunkMessage, participant?: RemoteParticipant) => {
      const { fileId, chunkIndex, totalChunks, chunk, metadata } = message;
      const chunks = fileChunksRef.current;

      if (!chunks.has(fileId)) {
        chunks.set(fileId, {
          chunks: new Array(totalChunks).fill(undefined),
          metadata,
          totalChunks
        });
      }

      const fileData = chunks.get(fileId)!;
      fileData.chunks[chunkIndex] = chunk;

      const allReceived = fileData.chunks.every((c: string | undefined) => c !== undefined);
      if (allReceived) {
        const completeData = fileData.chunks.join("");
        handlersRef.current.chat?.addChatMessage({
          id: message.id,
          senderId: message.senderId || 0,
          senderName: participant?.name || "Unknown",
          text: `${metadata.name}`,
          type: ChatType.FILE,
          timestamp: Date.now(),
          file: {
            name: metadata.name,
            size: metadata.size,
            type: metadata.type,
            data: completeData
          }
        });
        chunks.delete(fileId);
      }
    };

    /**
     * Main dispatcher for incoming data channel messages.
     * Routes messages to appropriate handlers based on 'type'.
     */
    const handleDataReceived = (payload: Uint8Array, participant?: RemoteParticipant) => {
      try {
        const decoder = new TextDecoder();
        const message = JSON.parse(decoder.decode(payload)) as DataMessage;
        const h = handlersRef.current;

        switch (message.type) {
          case "chat":
            h.chat?.addChatMessage({
              id: message.id,
              senderId: message.senderId || 0,
              senderName: participant?.name || "Unknown",
              text: message.text || "",
              type: ChatType.TEXT,
              timestamp: Date.now()
            });
            break;

          case "file":
            h.chat?.addChatMessage({
              id: message.id,
              senderId: message.senderId || 0,
              senderName: participant?.name || "Unknown",
              text: message.text || "",
              type: ChatType.FILE,
              timestamp: Date.now(),
              file: message.file
            });
            break;

          case "file-chunk":
            handleFileChunk(message as FileChunkMessage, participant);
            break;

          case "reaction":
          case "hand-raise":
          case "lower-hand-request":
            h.reactions.handleReactionDataReceived(message, participant);
            break;

          case "poll-create":
          case "poll-vote":
          case "poll-close":
            h.polls.handlePollDataReceived(message);
            break;

          case "notes-update":
          case "notes-presence":
            h.notes.handleNotesDataReceived(message);
            break;

          case "excalidraw-sync":
          case "excalidraw-request-sync":
          case "whiteboard-cursor":
          case "whiteboard-lock-state":
            h.whiteboard.handleWhiteboardDataReceived(message, participant);
            break;

          case "join-request":
            h.joinRequests.handleJoinRequestDataReceived(message, participant);
            playSound("request");
            break;

          case "meeting-end":
            if (h.meetingEnd?.handleMeetingEnd) {
              h.meetingEnd.handleMeetingEnd(message as MeetingEndPayload);
            }
            break;

          case "mute-request":
            if (h.muteRequest?.handleMuteRequest) {
              h.muteRequest.handleMuteRequest(message as MuteRequestPayload);
            }
            break;

          case "host-stop-screen-share":
            h.hostControls?.handleHostStopScreenShare();
            break;

          case "host-meeting-controls":
            h.hostControls?.handleHostMeetingControls(message);
            break;

          default:
            if (typeof message.type === "string" && message.type.startsWith("breakout-")) {
              h.breakout?.handleBreakoutDataReceived(message as Record<string, unknown>);
            }
            break;
        }
      } catch (err) {
        Logger.error("useDataChannelManager", "Error parsing data channel message:", err);
      }
    };

    room.on(RoomEvent.DataReceived, handleDataReceived);

    return () => {
      room.off(RoomEvent.DataReceived, handleDataReceived);
    };
  }, [room]); // Only depend on room - handlers accessed via ref
};
